import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { clearToken, setToken } from "@/lib/authToken";
/**
 * AuthContext basado en cookies httpOnly (única fuente de verdad).
 *
 * - Login: el backend setea cookie `ekspertium_auth` HttpOnly+Secure y
 *   devuelve los datos del usuario. NO guardamos nada sensible en
 *   localStorage para mitigar XSS.
 * - Al cargar la app: pedimos `/auth/me` — la cookie viaja sola; si
 *   responde 200 estamos autenticados; si 401, no.
 * - `dobilas_user` (solo email/nombre/role, no token) se cachea en
 *   sessionStorage para evitar el flash inicial de "logged out → loged in"
 *   mientras esperamos la respuesta de /me. Se borra al cerrar la pestaña.
 * - Logout: `/auth/logout` invalida la cookie en backend; limpiamos
 *   sessionStorage local.
 */
const AuthContext = createContext(null);

const VALID_ROLES = ["admin", "gestor_senior", "gestor_siniestros", "perito_interno", "perito_externo", "tramitador_telematico"];

const readCachedUser = () => {
  try {
    const u = JSON.parse(sessionStorage.getItem("dobilas_user"));
    if (u && VALID_ROLES.includes(u.role)) return u;
  } catch (err) {
    if (process.env.NODE_ENV === "development") console.debug("[auth] bad cache:", err);
  }
  return null;
};

const clearCache = () => {
  // sessionStorage para el bootstrap rápido
  sessionStorage.removeItem("dobilas_user");
  // localStorage por compatibilidad histórica (limpiar tokens viejos
  // potencialmente persistidos por versiones anteriores)
  try {
    localStorage.removeItem("dobilas_token");
    localStorage.removeItem("dobilas_user");
  } catch (err) { /* no localStorage disponible (modo privado) */ }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readCachedUser());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip /auth/me en rutas públicas — evita 401 ruidosos en consola
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isPublic = path === "/"
      || path.startsWith("/login")
      || path.startsWith("/verify")
      || path.startsWith("/forgot-password")
      || path.startsWith("/reset-password")
      || path.startsWith("/share");
    if (isPublic) {
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        try {
          sessionStorage.setItem("dobilas_user", JSON.stringify(r.data));
        } catch (err) { /* sessionStorage no disponible */ }
      })
      .catch((e) => {
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          clearCache();
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password, remember = false) => {
    const r = await api.post("/auth/login", { email, password, remember });
    // La cookie httpOnly ya está seteada por el backend (Set-Cookie).
    // Guardamos también el Bearer token en sessionStorage para clientes
    // legacy que aún construyen URLs con `?token=` (ver `lib/authToken.js`).
    setToken(r.data.access_token);
    try {
      sessionStorage.setItem("dobilas_user", JSON.stringify(r.data.user));
    } catch (err) { /* sessionStorage no disponible */ }
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");  // borra la cookie httpOnly en backend
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.debug("[auth] logout:", err);
    }
    clearToken();
    clearCache();
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={useMemo(() => ({ user, loading, login, logout }), [user, loading])}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
